import { api } from "@/lib/api";

export type SsoProviderId = "google" | "microsoft";

interface SsoProviderMeta {
  id: SsoProviderId;
  label: string;
  /** PrimeIcons class. */
  icon: string;
}

/** Supported providers, in display order. */
export const SSO_PROVIDERS: readonly SsoProviderMeta[] = [
  { id: "google", label: "Continue with Google", icon: "pi pi-google" },
  { id: "microsoft", label: "Continue with Microsoft", icon: "pi pi-microsoft" },
];

export interface EnabledSsoProvider extends SsoProviderMeta {
  loginUrl: string;
}

/**
 * Providers the user can sign in with. The backend exposes one start endpoint per
 * provider (`GET /api-front/auth/{provider}/login/`), which 302s to that identity
 * provider, so the URL is derived from the API base rather than configured on the FE.
 * Which providers a deployment actually has configured is not discoverable yet —
 * hitting an unconfigured one comes back as `#error=unknown_provider`.
 */
export function enabledSsoProviders(): EnabledSsoProvider[] {
  return SSO_PROVIDERS.map((provider) => ({
    ...provider,
    loginUrl: api.auth.apiUrl(["auth", provider.id, "login"]),
  }));
}

export type SsoCallbackResult = { status: "success"; token: string } | { status: "error"; code: string };

export function readSsoCallback(hash: string, search: string): SsoCallbackResult {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const query = new URLSearchParams(search);

  const token = fragment.get("token") ?? query.get("token");
  if (token) return { status: "success", token };

  return { status: "error", code: fragment.get("error") ?? query.get("error") ?? "" };
}

const SSO_ERROR_MESSAGES: Record<string, string> = {
  unknown_provider: "This sign-in provider isn't available.",
  invalid_request: "Sign-in was interrupted. Please try again.",
  provider_error: "The identity provider rejected the sign-in.",
  invalid_state: "Sign-in session expired. Please try again.",
};

/** User-facing message for a callback error code. */
export function ssoErrorMessage(code: string): string {
  if (!code) return "Sign-in failed.";
  return SSO_ERROR_MESSAGES[code] ?? `Sign-in failed (${code}).`;
}
